import { Text } from "@solomon-apps/shared/src/components/ui/text";
import { AnimatePresence, motion } from "motion/react";
import React from "react";

import type {
    FindSlice,
    NavSlice,
    RewardsDestinationVariant,
    RewardsSlice,
    SourceSlice,
    SubmitSlice,
    VerifySlice
} from "../steps";
import {
    isStepHidden,
    LAST_STEP,
    REWARDS_DESTINATION_VARIANT,
    STEP_COLLAPSE,
    STEP_FRAME_HEIGHT,
    WIZARD_STEPS
} from "../steps";

import { CollapsingBlock } from "./CollapsingBlock";
import { ReviewGroup } from "./ReviewGroup";
import { RewardsGroup } from "./RewardsGroup";
import { SubmitGroup } from "./SubmitGroup";
import { useLockedFrameHeight } from "./useLockedFrameHeight";
import { VerifyActionBar } from "./VerifyActionBar";
import { VerifyWalletGroup } from "./VerifyWalletGroup";

/* ── Right panel — the progressive step stack ─────────────────────────── */

interface StepFlowPanelProps {
    source: SourceSlice;
    find: FindSlice;
    verify: VerifySlice;
    rewards: RewardsSlice;
    submit: SubmitSlice;
    nav: NavSlice;
    /** Called once the in-content "Submit Position" flow completes. */
    onSubmitted: () => void;
    /** Rewards-destination treatment for step 1. Defaults to the flag-driven
     *  `REWARDS_DESTINATION_VARIANT`; the design-QA showcase pins each one. */
    rewardsVariant?: RewardsDestinationVariant;
}

/**
 * The right column — a single thin-bordered frame on the ground surface that
 * builds a vertical stack of step sections (completed steps collapse to a
 * compact summary, the current step is expanded + interactive). The Back/Next
 * nav lives in the left rail column (`PositionWalkthroughLayout`). The per-step content lives in `VerifyWalletGroup`
 * (the combined Add Address step) / `RewardsGroup` / `SubmitGroup`;
 * this file owns the frame, the step map, and the bottom-anchored action bars.
 */
export function StepFlowPanel({
    source,
    find,
    verify,
    rewards,
    submit,
    nav,
    onSubmitted,
    rewardsVariant = REWARDS_DESTINATION_VARIANT
}: StepFlowPanelProps): React.ReactElement {
    const { active } = nav;

    // FIXED-height frame: STEP_FRAME_HEIGHT, the tallest step, from first
    // paint and for the whole flow (content animates *inside* a static
    // frame). Only content that wraps taller at a narrow layout raises it. +8
    // leaves room
    // for the last button's light-bar (sits 4px below it). See the hook for the
    // debounce/clip rationale.
    const {
        ref: stackRef,
        extraRef: barRef,
        height: lockedHeight
    } = useLockedFrameHeight<HTMLDivElement, HTMLDivElement>({
        floor: STEP_FRAME_HEIGHT,
        pad: 8,
        // The verify (step 1) and submit (last step) action bars are both
        // anchored to the frame bottom (mt-auto) and are NOT part of the
        // measured stack. Reserve at least mt-8 (32px) of gap above whichever
        // is showing so the frame always seats both and the bar can never clip
        // or crowd the content.
        extraGap: 32,
        // The verify (step 0) and submit (last step) action bars are both
        // anchored to the frame bottom and not part of the measured stack.
        extraActive: active === 0 || active === LAST_STEP
    });

    return (
        // Right column — the bordered box, locked to its tallest-ever content so
        // it never resizes mid-flow. Its height sets the row height, which the
        // left rail column stretches to, so the Back/Next nav anchored to the
        // rail's bottom lines up with this box's bottom edge.
        <div className="flex flex-1 flex-col">
            <div
                className="flex flex-col overflow-hidden border border-line-base bg-surface-ground"
                style={{ height: lockedHeight }}
            >
                {/* No `layout` here — the frame is a fixed, clipped height and
                    each step's group animates its own height, so the stack just
                    reflows in flow. A `layout` animation on the stack double-
                    animates that reflow and produces the compress/overshoot
                    ("shoots too high, then settles") artifact. */}
                <div
                    ref={stackRef}
                    className="flex shrink-0 flex-col px-6 py-6"
                >
                    {/* Persistent question — pinned at the top while the
                        combined Add Address step (0) is current. On the
                        move to Rewards (step 1) it fades + collapses its own
                        height so the content below rises into its place. */}
                    <CollapsingBlock
                        show={active < 1}
                        transition={{
                            duration: STEP_COLLAPSE.moveDuration,
                            ease: STEP_COLLAPSE.ease
                        }}
                    >
                        <Text
                            variant="h3"
                            render={<h3 />}
                            className="text-foreground-base pb-4"
                        >
                            Add Address to Monitor
                        </Text>
                    </CollapsingBlock>
                    {WIZARD_STEPS.map((step, index) => {
                        if (index > active) return null;
                        if (isStepHidden(index, rewardsVariant)) return null;
                        return (
                            <StepSection
                                key={step.title}
                                index={index}
                                active={active}
                                first={index === 0}
                                source={source}
                                find={find}
                                verified={verify.status === "verified"}
                                ownership={
                                    verify.isDirect
                                        ? "pending"
                                        : verify.status === "verified"
                                          ? "verified"
                                          : null
                                }
                                rewards={rewards}
                                review={submit.review}
                                revealDelay={nav.revealDelay}
                                rewardsVariant={rewardsVariant}
                            />
                        );
                    })}
                </div>
                {/* Verify action row — lifted OUT of the measured stack above so
                    it can anchor to the bottom of the fixed-height frame (the
                    stack measures its natural height to lock the frame; this bar
                    rides in the leftover space via `mt-auto`). Shown only while
                    the verify step is current; fades in/out on enter/leave. */}
                <AnimatePresence>
                    {active === 0 && (
                        <motion.div
                            ref={barRef}
                            key="verify-actions"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{
                                duration: STEP_COLLAPSE.moveDuration,
                                ease: STEP_COLLAPSE.ease
                            }}
                            className="mt-auto px-6 pb-6"
                        >
                            <VerifyActionBar
                                sourceKind={source.kind}
                                verify={verify}
                            />
                        </motion.div>
                    )}
                    {/* Submit action — the last step's CTA, anchored to the
                        frame bottom the same way the verify bar is (lifted out
                        of the measured stack, rides `mt-auto`). */}
                    {active === LAST_STEP && (
                        <motion.div
                            ref={barRef}
                            key="submit-actions"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{
                                duration: STEP_COLLAPSE.moveDuration,
                                ease: STEP_COLLAPSE.ease
                            }}
                            className="mt-auto px-6 pb-6"
                        >
                            <SubmitGroup
                                submit={submit}
                                onSubmitted={onSubmitted}
                            />
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}

/* ── Right panel — progressive step section ─────────────────────────── */

interface StepSectionProps {
    index: number;
    active: number;
    /** First section has no top divider/spacing. */
    first: boolean;
    source: SourceSlice;
    find: FindSlice;
    /** Ownership verified — gates the discovered-holding preview in step 0. */
    verified: boolean;
    /** How ownership stands, for step 0's field badge: proven by the wallet
     *  (`verified`) or left to the distributor (`pending`, direct). */
    ownership: "verified" | "pending" | null;
    rewards: RewardsSlice;
    review: SubmitSlice["review"];
    /** Seconds to wait before this section fades in — lets the previous
     *  step's collapse finish before the next reveals. Only meaningful on the
     *  section's mount (when the step is newly reached). */
    revealDelay: number;
    rewardsVariant: RewardsDestinationVariant;
}

/**
 * A single step in the right-panel stack. Steps 0–1 render self-collapsing
 * groups (`VerifyWalletGroup` / `RewardsGroup`): expanded + interactive while
 * current, collapsed once passed (step 0 rests as the read-only source field;
 * step 1 folds to nothing — the last step's receipt restates what it captured).
 * The last step renders the `ReviewGroup` receipt; its action bar is anchored
 * to the frame bottom in `StepFlowPanel`. No section carries a `layout`
 * animation — each group animates its own height and the surrounding stack
 * reflows in normal flow inside the fixed, clipped frame; a `layout` animation
 * on top of that double-animates the reflow and overshoots.
 */
function StepSection({
    index,
    active,
    first,
    source,
    find,
    verified,
    ownership,
    rewards,
    review,
    revealDelay,
    rewardsVariant
}: StepSectionProps): React.ReactElement {
    const isCurrent = index === active;

    // Fade the section in on mount, after `revealDelay` — so a step that's
    // newly reached only appears once the previous step has finished
    // collapsing. (Only plays on mount; re-renders don't replay it.)
    const reveal = {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        transition: {
            duration: 0.45,
            ease: [0.16, 1, 0.3, 1],
            delay: revealDelay
        }
    } as const;

    // Step 0 — the combined Add Address step. Self-collapses so the
    // entered source stays visible as the read-only resting summary when the
    // user advances. The verify action row is anchored to the frame bottom
    // (`VerifyActionBar`), not rendered here.
    // On the last step the Review takes the address over as its first field,
    // so the summary folds away (`CollapsingBlock`).
    if (index === 0) {
        return (
            <CollapsingBlock
                show={active !== LAST_STEP}
                transition={{
                    duration: STEP_COLLAPSE.moveDuration,
                    ease: STEP_COLLAPSE.ease
                }}
            >
                <motion.section
                    {...reveal}
                    className={first ? "flex flex-col" : "mt-6 flex flex-col"}
                >
                    <VerifyWalletGroup
                        collapsed={!isCurrent}
                        verified={verified}
                        ownership={ownership}
                        source={source}
                        find={find}
                    />
                </motion.section>
            </CollapsingBlock>
        );
    }

    // Step 1 — set rewards destination. Collapses to nothing: the last step's
    // receipt restates the destination, so keeping the field as a resting
    // summary would show the same address twice. Its top gap lives inside its
    // own collapsing box (no `mt-6` here) so nothing leaves a double gap where
    // the step folded away. `RewardsGroup` owns its own height animation — no
    // `layout` here.
    if (index === 1) {
        return (
            <motion.section {...reveal} className="flex flex-col">
                <RewardsGroup
                    variant={rewardsVariant}
                    collapsed={!isCurrent}
                    positionAddress={rewards.defaultAddress || source.address}
                    defaultLabel={rewards.defaultLabel}
                    multisigSummary={source.multisigSummary}
                    destination={rewards.destination}
                    onDestinationChange={rewards.onDestinationChange}
                />
            </motion.section>
        );
    }

    // Step 2 — the review receipt. Never collapses (it's the last step), and
    // it is the frame's first content: the source summary above folds away
    // and the Review restates the address as its first field. The register
    // action bar is anchored to the frame bottom in `StepFlowPanel`, not
    // rendered here.
    return (
        <motion.section {...reveal} className="flex flex-col">
            <ReviewGroup
                review={review}
                sourceAddress={source.address}
                sourceLabel={source.addressLabel}
            />
        </motion.section>
    );
}
